import { useRef, useCallback } from 'react';

/**
 * Hook that provides text-to-speech voice guidance using the Web Speech API.
 *
 * Designed for hands-free self-capture: speaks alignment corrections
 * and capture events so the user doesn't need to look at the screen.
 *
 * - Alignment messages are debounced (3.5s minimum gap)
 * - Capture messages bypass debounce and cancel current speech
 * - speakSequence() queues multiple messages with delays
 * - Cleans up on dispose()
 */
export function useVoiceGuide() {
  const lastSpokeRef = useRef(0);
  const sequenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supported =
    typeof window !== 'undefined' && 'speechSynthesis' in window;

  /**
   * Speak a message via TTS.
   * @param text The message to speak
   * @param priority 'alignment' messages are debounced; 'capture' messages play immediately
   */
  const speak = useCallback(
    (text: string, priority: 'alignment' | 'capture' = 'alignment') => {
      if (!supported) return;

      const now = Date.now();

      // Alignment messages debounced to avoid rapid-fire corrections
      if (priority === 'alignment' && now - lastSpokeRef.current < 3500) {
        return;
      }

      // Cancel any in-progress speech so new message plays immediately
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1.05;
      utterance.volume = 0.9;
      utterance.lang = 'en-US';

      window.speechSynthesis.speak(utterance);
      lastSpokeRef.current = now;
    },
    [supported]
  );

  /**
   * Queue multiple messages to speak in sequence with delays between them.
   * Each message waits for the previous to finish (estimated) before speaking.
   * @param messages Array of messages to speak in order
   * @param delayMs Delay between messages in ms (default 800)
   */
  const speakSequence = useCallback(
    (messages: string[], delayMs = 800) => {
      if (!supported || messages.length === 0) return;

      // Cancel any in-flight sequence
      if (sequenceTimerRef.current) {
        clearTimeout(sequenceTimerRef.current);
        sequenceTimerRef.current = null;
      }

      window.speechSynthesis.cancel();

      let index = 0;
      function speakNext() {
        if (index >= messages.length) return;
        const text = messages[index]!;
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 1.15;
        utterance.volume = 0.9;
        utterance.lang = 'en-US';

        utterance.onend = () => {
          index++;
          if (index < messages.length) {
            sequenceTimerRef.current = setTimeout(speakNext, delayMs);
          }
        };

        window.speechSynthesis.speak(utterance);
        lastSpokeRef.current = Date.now();
      }

      speakNext();
    },
    [supported]
  );

  /** Stop all speech immediately. */
  const cancel = useCallback(() => {
    if (sequenceTimerRef.current) {
      clearTimeout(sequenceTimerRef.current);
      sequenceTimerRef.current = null;
    }
    if (supported) window.speechSynthesis.cancel();
  }, [supported]);

  /** Cleanup — cancel speech. Call on unmount. */
  const dispose = useCallback(() => {
    cancel();
  }, [cancel]);

  return { speak, speakSequence, cancel, isSupported: supported, dispose };
}
